import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import { contactPath } from '../crmPaths';
import { count } from '../manage/manage';
import { SegmentChoice, SegmentSelect, TagSelect } from '../outreach/Pickers';
import { StepIcon } from './StepIcon';
import { CanvasReach } from './WorkflowCanvas';
import { changedCount, describeWorkflowError, formatDuration, STEP_META, StepType, TriggerType } from './workflowModel';
import { CRM_ENROLL_COUNT, CRM_ENROLL_IN_WORKFLOW, CRM_SIMULATE_WORKFLOW } from './workflows.queries';

/* ------------------------------------------------------------------ *
 * Who: everyone on a list or saved filter, or everyone with a tag
 * ------------------------------------------------------------------ */

export type Audience = { kind: 'list'; segment: SegmentChoice | null } | { kind: 'tag'; tag: { id: number; name: string } | null };

/** The contact filter an audience stands for, or null until one is chosen. */
export const audienceFilter = (a: Audience) =>
  a.kind === 'list' ? (a.segment ? { segments: { anyOf: [a.segment.id] } } : null) : a.tag ? { tags: { anyOf: [a.tag.id] } } : null;

export const audienceName = (a: Audience) => (a.kind === 'list' ? a.segment?.name : a.tag?.name) ?? '';

export const AudiencePicker = ({ value, onChange, disabled }: { value: Audience; onChange: (a: Audience) => void; disabled?: boolean }) => (
  <Box sx={{ display: 'grid', gap: 1.25 }}>
    <ToggleButtonGroup
      exclusive
      size="small"
      fullWidth
      value={value.kind}
      disabled={disabled}
      onChange={(_, kind) => kind && onChange(kind === 'list' ? { kind: 'list', segment: null } : { kind: 'tag', tag: null })}
    >
      <ToggleButton value="list">A list or saved filter</ToggleButton>
      <ToggleButton value="tag">Everyone with a tag</ToggleButton>
    </ToggleButtonGroup>
    {value.kind === 'list' ? (
      <SegmentSelect label="List or saved filter" value={value.segment} onChange={(segment) => onChange({ kind: 'list', segment })} disabled={disabled} />
    ) : (
      <TagSelect label="Tag" value={value.tag?.id ?? null} valueName={value.tag?.name} onChange={(tag) => onChange({ kind: 'tag', tag })} disabled={disabled} />
    )}
  </Box>
);

/* ------------------------------------------------------------------ *
 * Simulation: who it would enrol, what each step could reach
 * ------------------------------------------------------------------ */

export interface Simulation {
  matched: number;
  wouldEnroll: number;
  steps: Array<{ position: number; type: StepType; afterMinutes: number | null; reach: number }>;
  sample: Array<{ id: number; fullName: string | null; canEmail: boolean; canText: boolean; hasAccount: boolean }>;
}

export const reachOf = (sim: Simulation | null | undefined): CanvasReach | null =>
  sim ? { wouldEnroll: sim.wouldEnroll, byPosition: new Map(sim.steps.map((s) => [s.position, s.reach])) } : null;

const Card = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', p: 2, display: 'grid', gap: 1.5 }}>{children}</Box>
);

interface SimulationCardProps {
  workflowId: number;
  triggerType: TriggerType | 'EVENT';
  /** The steps changed and are not saved: the simulation reads the saved ones. */
  unsaved: boolean;
  onResult: (sim: Simulation | null) => void;
}

export const SimulationCard = ({ workflowId, triggerType, unsaved, onResult }: SimulationCardProps) => {
  const manual = triggerType === 'MANUAL';
  const [audience, setAudience] = useState<Audience>({ kind: 'list', segment: null });
  const [run, { data, loading, error }] = useLazyQuery<{ crmSimulateWorkflow: Simulation }>(CRM_SIMULATE_WORKFLOW, { fetchPolicy: 'network-only' });
  const sim = data?.crmSimulateWorkflow;
  const filter = audienceFilter(audience);

  useEffect(() => onResult(sim ?? null), [sim, onResult]);

  const simulate = () => run({ variables: { id: workflowId, filter: manual ? filter : null } });

  return (
    <Card>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScienceOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography sx={{ fontWeight: 650 }}>Simulate</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {manual
          ? 'Who would come in if you enrolled these people now, and what each message could reach. Nothing is enrolled or sent.'
          : 'Who the trigger would enrol now, and what each message could reach. Nothing is enrolled or sent.'}
      </Typography>
      {manual && <AudiencePicker value={audience} onChange={setAudience} />}
      {unsaved && <Alert severity="info">It simulates the saved workflow: save your changes first to see them.</Alert>}
      <Button
        variant="outlined"
        onClick={simulate}
        disabled={loading || (manual && !filter)}
        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <ScienceOutlinedIcon />}
      >
        {sim ? 'Simulate again' : 'Simulate'}
      </Button>
      {error && <Alert severity="error">{describeWorkflowError(error, 'The simulation could not run.')}</Alert>}
      {sim && <SimulationResult sim={sim} />}
    </Card>
  );
};

const SimulationResult = ({ sim }: { sim: Simulation }) => {
  const kept = sim.matched - sim.wouldEnroll;
  const messages = sim.steps.filter((s) => STEP_META[s.type]?.sends);

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, background: `linear-gradient(135deg, ${alpha('#4f46e5', 0.08)}, ${alpha('#7c3aed', 0.06)})` }}>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 750, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{count(sim.wouldEnroll)}</Typography>
        <Typography variant="body2">would be enrolled</Typography>
        <Typography variant="caption" color="text.secondary">
          {kept > 0 ? `${count(sim.matched)} match; ${count(kept)} are kept out by the guards (been in it before, an exit tag, or merged away).` : `${count(sim.matched)} match.`}
        </Typography>
      </Box>

      {messages.length > 0 && (
        <Box sx={{ display: 'grid', gap: 0.75 }}>
          {messages.map((s) => {
            const pct = sim.wouldEnroll > 0 ? (s.reach / sim.wouldEnroll) * 100 : 0;
            const tone = STEP_META[s.type].tone;
            return (
              <Box key={s.position}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.8125rem' }}>
                  <Box sx={{ color: tone, display: 'flex' }}>
                    <StepIcon type={s.type} fontSize={16} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    Step {s.position + 1}, {STEP_META[s.type].label.toLowerCase()}
                    {s.afterMinutes ? <Box component="span" sx={{ color: 'text.secondary' }}> · {formatDuration(s.afterMinutes)} in</Box> : null}
                  </Box>
                  <b style={{ fontVariantNumeric: 'tabular-nums' }}>{count(s.reach)}</b>
                </Box>
                <Box aria-hidden sx={{ height: 5, mt: 0.5, borderRadius: 3, bgcolor: '#eef1f5', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: tone, transition: 'width 400ms' }} />
                </Box>
              </Box>
            );
          })}
          <Typography variant="caption" color="text.secondary">
            The rest are not opted in, have no address or number, or are do-not-contact: they carry on to the next step without the message.
          </Typography>
        </Box>
      )}

      {sim.sample.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            Some of them
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 0.5 }}>
            {sim.sample.map((p) => (
              <Box component="li" key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.8125rem' }}>
                <Link component={RouterLink} to={contactPath(p.id)} underline="hover" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.fullName || `Contact #${p.id}`}
                </Link>
                <Can on={p.canEmail} label="email" icon={<MailOutlineIcon />} />
                <Can on={p.canText} label="text" icon={<SmsOutlinedIcon />} />
                <Can on={p.hasAccount} label="app" icon={<PhoneIphoneIcon />} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Can = ({ on, label, icon }: { on: boolean; label: string; icon: React.ReactElement }) => (
  <Tooltip title={on ? `Can ${label === 'app' ? 'get app notifications' : `be ${label}ed`}` : `Cannot ${label === 'app' ? 'get app notifications' : `be ${label}ed`}`}>
    <Box component="span" aria-label={`${label}: ${on ? 'yes' : 'no'}`} sx={{ display: 'flex', color: on ? '#059669' : '#cbd5e1', '& svg': { fontSize: 16 } }}>
      {icon}
    </Box>
  </Tooltip>
);

/* ------------------------------------------------------------------ *
 * Enrolling by hand (NationBuilder's "add to automation")
 * ------------------------------------------------------------------ */

interface EnrollDialogProps {
  workflowId: number;
  workflowName: string;
  onClose: () => void;
  onEnrolled: (result: { matched: number; enrolled: number; skipped: number }) => void;
}

export const EnrollDialog = ({ workflowId, workflowName, onClose, onEnrolled }: EnrollDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [audience, setAudience] = useState<Audience>({ kind: 'list', segment: null });
  const [expected, setExpected] = useState<number | null>(null);
  const filter = audienceFilter(audience);

  const [countThem, counted] = useLazyQuery<{ crmContactCounts: { total: number } }>(CRM_ENROLL_COUNT, { fetchPolicy: 'network-only' });
  const [enrol, enrolling] = useMutation(CRM_ENROLL_IN_WORKFLOW);

  useEffect(() => {
    setExpected(null);
    if (filter) countThem({ variables: { filter } }).then(({ data }) => data && setExpected(data.crmContactCounts.total));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const confirm = async () => {
    if (!filter || expected === null) return;
    try {
      const { data } = await enrol({ variables: { id: workflowId, filter, expectedCount: expected } });
      onEnrolled(data.crmEnrollInWorkflow);
    } catch (error) {
      const now = changedCount(error);
      if (now !== undefined) setExpected(now);
    }
  };

  return (
    <Dialog open onClose={enrolling.loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-enrol-title">
      <DialogTitle id="crm-enrol-title">Enrol people in {workflowName}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <AudiencePicker value={audience} onChange={setAudience} disabled={enrolling.loading} />
        {filter && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid', borderColor: 'divider' }}>
            {counted.loading || expected === null ? (
              <Typography variant="body2" color="text.secondary">
                Counting…
              </Typography>
            ) : (
              <>
                <Typography sx={{ fontWeight: 650 }}>
                  {count(expected)} {expected === 1 ? 'person matches' : 'people match'} {audienceName(audience)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The guards keep out anyone who has been in it before (unless re-entry is on), carries an exit tag, or was merged away. Messages still check
                  consent as each one goes.
                </Typography>
              </>
            )}
          </Box>
        )}
        {counted.error && <Alert severity="error">{describeWorkflowError(counted.error, 'Could not count them.')}</Alert>}
        {enrolling.error && <Alert severity={changedCount(enrolling.error) !== undefined ? 'warning' : 'error'}>{describeWorkflowError(enrolling.error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={enrolling.loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={confirm}
          disabled={!filter || expected === null || expected === 0 || enrolling.loading}
          startIcon={enrolling.loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {expected ? `Enrol ${count(expected)}` : 'Enrol'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ *
 * Start, pause, end, archive, delete: confirmed
 * ------------------------------------------------------------------ */

interface ConfirmDialogProps {
  title: string;
  children: React.ReactNode;
  confirm: string;
  danger?: boolean;
  running: boolean;
  error?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export const ConfirmDialog = ({ title, children, confirm, danger, running, error, onConfirm, onClose }: ConfirmDialogProps) => (
  <Dialog open onClose={running ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="crm-confirm-title">
    <DialogTitle id="crm-confirm-title">{title}</DialogTitle>
    <DialogContent dividers>
      <Stack spacing={1.5} sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
        {children}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 1.5 }}>
      <Button onClick={onClose} disabled={running}>
        Cancel
      </Button>
      <Button
        variant="contained"
        color={danger ? 'error' : 'primary'}
        onClick={onConfirm}
        disabled={running}
        startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {confirm}
      </Button>
    </DialogActions>
  </Dialog>
);
